import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber, InputNumberChangeEvent, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Nullable } from "primereact/ts-helpers";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { motion, AnimatePresence } from "framer-motion";

import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import TextInput from "../../Components/TextInput";
import NumberInput from "../../Components/NumberInput";
import DatePicker from "../../Components/DatePicker";
import Textarea from "../../Components/TextArea";
import { goToTop } from "../../Components/goToTop";
import { getLoginUserRoleName, UserRoleName } from "../../Utils/LoginUserRoleName";
import { getAppCurrencyType } from "../../Utils/Common";
import { formatDate, parseStringToDate } from "../../Utils/getDate";
import { imageFileExtension } from "../../Api/FileConstant";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import QuotationReportConfigModal from "./QuotationReportConfigModal";
import {
    QuotationList as QuotationListType,
    QuotationNoteList,
    Quotation as QuotationType,
    SOWDetailDataList,
    SOWDetailDataSubItemList,
    SOWSimpleDataList,
    SOWSimpleDataSubItemList,
    MaterialDataUnit,
    QuotationCoverPage
} from "../../Controllers/Types/Quotation/QuotationType";
import { QuotationNote as QuotationNoteType } from "../../Controllers/Types/Quotation/QuotationNoteType";
import { fetchQuotationData, fetchQuotationNoteList, fetchMaterialDataUnit } from "../../Controllers/Thunks/QuotationThunks";
import { createQuotationCoverPage, createQuotationNote, createQuotationNoteMultiple, createQuotationSubItemDetail, createQuotationSubItemDetailAdd, createQuotationSubItemDetailEdit, createQuotationSubItemSimple, createQuotationSubItemSimpleItemData, createQuotationTerms, deleteQuotationNote, deleteQuotationSubItem, updateQuotationNote, updateQuotationSubItemSimpleItemData } from "../../Api/Quotation/QuotationApiService";
import { checkModelSelected, checkNumber, checkText } from "../../Utils/Validator";

type SimpleEditingValues = {
    material: number | null;
    labour: number | null;
    total: number | null;
};

type DetailEditingValues = {
    labour: number | null;
    quantity: number | null;
    unitPrice: number | null;
};

const QuotationDetail: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const currencySymbol = getAppCurrencyType();

    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [coverPageLoading, setCoverPageLoading] = useState<boolean>(false);
    const [addLoading, setAddLoading] = useState<boolean>(false);
    const [addMoreLoading, setAddMoreLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [showSideBar, setShowSideBar] = useState<boolean>(false);

    const quotationNo: number | null = location?.state?.quotationNo;
    const GlobalQuotationDetail: QuotationListType | null = location?.state?.quotationDetail;
    const globalQuotation: QuotationType | null = location?.state?.quotation;

    const [quotation, setQuotation] = useState<QuotationType | null>(null);
    const [quotationDetail, setQuotationDetails] = useState<QuotationListType | null>(null);


    const loadingQuotation: boolean = useAppSelector((state) => state?.quotationList?.loadingQuotation);
    // const quotations: QuotationType[] = useAppSelector((state) => state?.quotationList?.quotations);
    const errorQuotation: string | null = useAppSelector((state) => state?.quotationList?.errorQuotation);

    const loadingMaterialDataUnit: boolean = useAppSelector((state) => state?.quotationList?.loadingMaterialDataUnit);
    const materialDataUnits: MaterialDataUnit[] = useAppSelector((state) => state?.quotationList?.materialDataUnits);

    const loadingQuotationNoteList: boolean = useAppSelector((state) => state?.quotationList?.loadingQuotationNoteList);
    const quotationNoteList: QuotationNoteType[] = useAppSelector((state) => state?.quotationList?.quotationNoteList);
    const errorQuotationNoteList: string | null = useAppSelector((state) => state?.quotationList?.errorQuotationNoteList);

    const [quotationDetailViewState, setQuotationDetailViewState] = useState<'Cover page' | 'Note' | 'SOW' | 'Terms'>('Cover page');

    const [showReportConfigModal, setShowReportConfigModal] = useState<boolean>(false);

    const [initalLoad, setInitalLoad] = useState<boolean>(true);

    const [canEdit, setCanEdit] = useState<boolean>(false);


    /* Cover page */
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
    const [companyName, setCompanyName] = useState<string>('');
    const [projectDescription, setProjectDescription] = useState<string>('');
    const [projectAddress, setProjectAddress] = useState<string>('');
    const [clientName, setClientName] = useState<string>('');
    const [haveCoverPageDetails, setHaveCoverPageDetails] = useState<boolean>(false);
    const [coverPageUpdateHasChanges, setCoverPageUpdateHasChanges] = useState<boolean>(false);

    const [companyLogoError, setCompanyLogoError] = useState<string>('');
    const [companyNameError, setCompanyNameError] = useState<string>('');
    const [projectDescriptionError, setProjectDescriptionError] = useState<string>('');
    const [projectAddressError, setProjectAddressError] = useState<string>('');
    const [clientNameError, setClientNameError] = useState<string>('');

    const [coverPageData, setCoverPageData] = useState<QuotationCoverPage | null>(null);
    const [showCoverPagePreviewModal, setShowCoverPagePreviewModal] = useState<boolean>(false);
    /*  */

    /* Note */
    const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
    const [showNoteListModal, setShowNoteListModal] = useState<boolean>(false);
    // default notes
    const [quotationNotesData, setQuotationNotesData] = useState<QuotationNoteType[]>([]);
    // default selected notes
    const [selectedNoteDefault, setSelectedNoteDefault] = useState<QuotationNoteType | null>(null);
    const [selectedNotes, setSelectedNotes] = useState<QuotationNoteType[]>([]);

    // quotation edit option note
    const [selectedNote, setSelectedNote] = useState<QuotationNoteList | null>(null);

    const [noteUpdateHasChanges, setNoteUpdateHasChanges] = useState<boolean>(false);
    const [note, setNote] = useState<string>('');
    const [noteError, setNoteError] = useState<string>('');
    /*  */

    /* SOW */
    const [sowViewOption, setSowViewOption] = useState<'Simple' | 'Detail'>('Simple');

    // Simple
    const [activeSimpleItem, setActiveSimpleItem] = useState<SOWSimpleDataList | null>(null);
    const [selectedSimpleSubItem, setSelectedSimpleSubItem] = useState<SOWSimpleDataSubItemList | null>(null);
    const [showSimpleItemModal, setShowSimpleItemModal] = useState<boolean>(false);

    const [editableSimpleItemId, setEditableSimpleItemId] = useState<number | null>(null);
    const [editingSimpleSubItemValues, setEditingSimpleSubItemValues] = useState<Record<number, SimpleEditingValues>>({});
    const editingSimpleValuesRef = useRef<Record<number, SimpleEditingValues>>({});
    const [loadingSimpleSubItemId, setLoadingSimpleSubItemId] = useState<number | null>(null);
    const [loadingSimpleField, setLoadingSimpleField] = useState<"material" | "labour" | "total" | null>(null);

    const [simpleSubItemUpdateHasChanges, setsimpleSubItemUpdateHasChanges] = useState<boolean>(false);

    const [simpleItemName, setSimpleItemName] = useState<string>('');
    const [material, setMaterial] = useState<number>(0);
    const [labour, setLabour] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);

    const [simpleItemNameError, setSimpleItemNameError] = useState<string>('');
    const [materialError, setMaterialError] = useState<string>('');
    const [labourError, setLabourError] = useState<string>('');
    const [totalError, setTotalError] = useState<string>('');

    const [materialDisabled, setMaterialDisabled] = useState<boolean>(false);
    const [labourDisabled, setLabourDisabled] = useState<boolean>(false);
    const [totalDisabled, setTotalDisabled] = useState<boolean>(false);

    // Detail
    const [activeDetailItem, setActiveDetailItem] = useState<SOWDetailDataList | null>(null);
    const [selectedDetailSubItem, setSelectedDetailSubItem] = useState<SOWDetailDataSubItemList | null>(null);
    const [showDetailItemModal, setShowDetailItemModal] = useState<boolean>(false);

    const [detailItemName, setDetailItemName] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(0);
    const [unit, setUnit] = useState<MaterialDataUnit | null>(null);
    const [unitPrice, setUnitPrice] = useState<number>(0);
    const [materialCost, setMaterialCost] = useState<number>(0);
    const [labourCost, setLabourCost] = useState<number>(0);

    const [editableDetailItemId, setEditableDetailItemId] = useState<number | null>(null);
    const [editingDetailSubItemValues, setEditingDetailSubItemValues] = useState<Record<number, DetailEditingValues>>({});
    const editingDetailValuesRef = useRef<Record<number, DetailEditingValues>>({});
    const [loadingDetailSubItemId, setLoadingDetailSubItemId] = useState<number | null>(null);
    const [loadingDetailField, setLoadingDetailField] = useState<"labour" | "quantity" | "unit price" | null>(null);

    const [detailItemNameError, setDetailItemNameError] = useState<string>('');
    const [quantityError, setQuantityError] = useState<string>('');
    const [unitError, setUnitError] = useState<string>('');
    const [unitPriceError, setUnitPriceError] = useState<string>('');
    const [materialCostError, setMaterialCostError] = useState<string>('');
    const [labourCostError, setLabourCostError] = useState<string>('');

    /*  */

    /* Terms */
    const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
    const [termsAddState, setTermsAddState] = useState<'Advance payment' | 'Start date' | 'Duration' | null>(null);
    const [advancePayment, setAdvancePayment] = useState<number | null>(0);
    const [startDate, setStartDate] = useState<Nullable<Date>>(null);
    const [durationOption, setDurationOption] = useState<'Week' | 'Month'>('Week');
    const [duration, setDuration] = useState<number>(0);

    const [advancePaymentError, setAdvancePaymentError] = useState<string>('');
    const [startDateError, setStartDateError] = useState<string>('');
    const [durationError, setDurationError] = useState<string>('');

    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    // Get logged in user role name
    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };
        fetchUserRole();
    }, []);


    // load globlal state quotations
    useEffect(() => {
        dispatch(fetchQuotationData());
        dispatch(fetchQuotationNoteList());
        dispatch(fetchMaterialDataUnit());

        setInitalLoad(false);
    }, [dispatch]);

    // this is get data from state list and set projectlist
    const quatations = useAppSelector((state) => state.quotationList.quotations);


    useEffect(() => {
        const list = quatations.filter(
            (e) => e.id === globalQuotation?.id
        );
        if (list?.length > 0) {
            setQuotation(list[0] as QuotationType);
        }
    }, [quatations]);

    // now get current quotation listtype

    useEffect(() => {
        const list = quotation?.quotation_data.filter(
            (e) => e.id === GlobalQuotationDetail?.id
        );
        if (list && list?.length > 0) {
            setQuotationDetails(list[0] as QuotationListType);
        }
    }, [quotation]);

    useEffect(() => {
        if (quotationNoteList && quotationNoteList?.length > 0) {
            const activeNoteList = quotationNoteList?.filter(data => data?.status === 1);

            setQuotationNotesData(activeNoteList);
        } else {
            setQuotationNotesData([]);
        }
    }, [quotationNoteList]);


    const handleChangeQuotationDetailViewState = (state: 'Cover page' | 'Note' | 'SOW' | 'Terms') => {
        setQuotationDetailViewState(state);
        handleToggleSideBar();
    }


    /* Cover page */
    useEffect(() => {
        if (quotationDetail) {
            setCanEdit(quotationDetail.can_edit);

            const coverPage = quotationDetail?.cover_page_data;

            if (coverPage) {
                setHaveCoverPageDetails(true);
                setCoverPageData(coverPage);

                setCompanyLogo(coverPage?.comapany_logo || '');
                setCompanyName(coverPage?.comapany_name || '');
                setProjectDescription(coverPage?.project_description || '');
                setProjectAddress(coverPage?.project_address || '');
                setClientName(coverPage?.client_name || '');
            } else {
                setHaveCoverPageDetails(false);
                setCoverPageData(null);

                setCompanyLogo('');
                setCompanyLogoFile(null);
                setCompanyName('');
                setProjectDescription('');
                setProjectAddress('');
                setClientName('');
            }
        }
    }, [quotationDetail]);

    useEffect(() => {
        if (quotationDetail) {
            const coverPageData = quotationDetail?.cover_page_data;

            if (companyLogo !== coverPageData?.comapany_logo) {
                setCoverPageUpdateHasChanges(true);
            } else if (companyName !== coverPageData?.comapany_name) {
                setCoverPageUpdateHasChanges(true);
            } else if (projectDescription !== coverPageData?.project_description) {
                setCoverPageUpdateHasChanges(true);
            } else if (projectAddress !== coverPageData?.project_address) {
                setCoverPageUpdateHasChanges(true);
            } else if (clientName !== coverPageData?.client_name) {
                setCoverPageUpdateHasChanges(true);
            } else {
                setCoverPageUpdateHasChanges(false);
            }
        }
    }, [
        quotationDetail,
        companyLogo,
        companyName,
        projectDescription,
        projectAddress,
        clientName
    ]);

    const handleResetCoverPageFields = () => {
        setCompanyLogo('');
        setCompanyLogoFile(null);
        setCompanyName('');
        setProjectDescription('');
        setProjectAddress('');
        setClientName('');
    }

    const handleResetCoverPageErrors = () => {
        setCompanyLogoError('');
        setCompanyNameError('');
        setProjectDescriptionError('');
        setProjectAddressError('');
        setClientNameError('');
    }


    // validations options

    // project reference validation here.
    function checkCredentials(): boolean {
        const address = checkText(companyName, true, setCompanyNameError);
        const refe = checkText(projectAddress, true, setProjectAddressError);
        const client = checkText(clientName, true, setClientNameError);
        return address && refe && client;
    }

    // on chnage validation clear.
    useEffect(() => {
        checkText(projectAddress, false, setProjectAddressError);
    }, [projectAddress]);


    useEffect(() => {
        checkText(companyName, false, setCompanyNameError);
    }, [companyName]);

    useEffect(() => {
        checkText(clientName, false, setClientNameError);
    }, [clientName]);


    // Cover page save api
    const handleSaveCoverPageDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setCoverPageLoading(true);
            const data = await createQuotationCoverPage(
                quotation?.id!,
                companyName,
                projectDescription,
                projectAddress,
                clientName,
                companyLogoFile!
            );
            if (data.statusCode === 200) {
                setCoverPageLoading(false);
                handleResetCoverPageFields();
                handleResetCoverPageErrors();
                dispatch(fetchQuotationData());

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Cover page details added successfully.',
                        life: 3000
                    });
                }
            } else {
                setCoverPageLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding the cover page details.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleUploadCompanyLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = (event: ProgressEvent<FileReader>) => {
                if (event.target && typeof event.target.result === 'string') {
                    setCompanyLogo(event.target.result);

                    const file = e.target.files?.[0];
                    setCompanyLogoFile(file!);
                }
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleClearCompanyLogo = () => {
        setCompanyLogo('');
        setCompanyLogoFile(null);

        if (quotationDetail?.cover_page_data?.comapany_logo) {
            setCompanyLogo(quotationDetail?.cover_page_data?.comapany_logo);
        } else {
            setCompanyLogo('');
        }
    }

    const handleViewCoverPagePreview = () => {
        setShowCoverPagePreviewModal(true);
    }

    const handleCloseCoverPagePreviewModal = () => {
        setShowCoverPagePreviewModal(false);
    }

    const coverPagePreviewModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Cover page
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseCoverPagePreviewModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );
    /*  */


    /* Note */
    useEffect(() => {
        if (note !== selectedNote?.note) {
            setNoteUpdateHasChanges(true);
        } else {
            setNoteUpdateHasChanges(false);
        }
    }, [note, selectedNote]);

    const handleAddNote = () => {
        setDataState('Add');
        setShowNoteModal(true);
    }

    const handleCloseNoteModal = () => {
        setShowNoteModal(false);
        handleResetNoteFields();
        handleResetNoteErrors();
    }

    const handleResetNoteFields = () => {
        setNote('');
        setSelectedNotes([]);
    }

    const handleResetNoteErrors = () => {
        setNoteError('');
    }

    function checkCredentialsNote(): boolean {
        const noteText = checkText(note, true, setNoteError);
        return noteText;
    }


    useEffect(() => {
        checkText(note, false, setNoteError);
    }, [note]);


    /* single note selected */
    useEffect(() => {
        if (selectedNotes && selectedNotes.length === 1) {
            setSelectedNoteDefault(selectedNotes[0]);
        } else {
            setSelectedNoteDefault(null);
        }
    }, [selectedNotes]);

    // Note create api database function.
    const handleCreateNote = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsNote()) {
            setLoading(true);
            const data = await createQuotationNote(
                quotationDetail?.id!,
                selectedNoteDefault?.id!,
                selectedNoteDefault != null ? "Default" : "Custom",
                note,
            );
            if (data.statusCode === 200) {
                setLoading(false);
                handleCloseNoteModal();
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Note created successfully.',
                        life: 3000
                    });
                }
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating the note.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleEditNote = (noteData: QuotationNoteList) => {
        setSelectedNote(noteData)
        setDataState('Edit');
        setShowNoteModal(true);
        setNote(noteData?.note || '');
    }

    // Note update api
    const handleUpdateNote = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsNote()) {
            setLoading(true);
            const data = await updateQuotationNote(
                selectedNote?.id!,
                selectedNoteDefault?.id!,
                selectedNoteDefault != null ? "Default" : "Custom",
                note,
            );
            if (data.statusCode === 200) {
                setLoading(false);
                handleCloseNoteModal();
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Note updated successfully.',
                        life: 3000
                    });
                }
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the note.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleDeleteNote = (noteData: QuotationNoteList) => {
        const noteId = noteData?.id;

        confirmDialog({
            message: 'Are you sure you want to delete this note?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteNote(noteId),
        });
    }

    // Note delete api done
    const deleteNote = async (noteId: number) => {
        const result = await deleteQuotationNote(noteId);

        if (result.statusCode === 200) {
            dispatch(fetchQuotationData());
            handleCloseNoteModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Note deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the note.',
                    life: 3000
                });
            }
        }
    }

    const noteModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} note
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseNoteModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const noteModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseNoteModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateNote : handleUpdateNote}
                loading={loading} className="custom_btn primary"
                disabled={dataState === 'Edit' && !noteUpdateHasChanges}
            />
        </div>
    );

    const handleViewNoteList = () => {
        setShowNoteListModal(true);
        setShowNoteModal(false);
    }

    const handleCloseNoteListModal = () => {
        setShowNoteListModal(false);
        setSelectedNotes([]);
        setShowNoteModal(true);
    }


    const handleAddSingleNote = () => {
        if (selectedNoteDefault && selectedNoteDefault != null) {
            setNote(selectedNoteDefault.note);

            setShowNoteListModal(false);
            setShowNoteModal(true);
        } else {
            setNote("");
        }
    }

    // Note multimple add api
    const handleAddMultipleNotes = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedNotes && selectedNotes.length > 0) {
            let list: string[] = selectedNotes.map((e) => e.id.toString());
            setAddLoading(true);
            const data = await createQuotationNoteMultiple(
                quotationDetail?.id!,
                list
            );
            if (data.statusCode === 200) {
                setAddLoading(false);
                handleCloseNoteModal();
                setShowNoteListModal(false);
                setSelectedNotes([]);
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Note(s) added successfully.',
                        life: 3000
                    });
                }
            } else {
                setAddLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding the note(s).',
                        life: 3000
                    });
                }
            }
        }
    }

    const noteListModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Choose note
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseNoteListModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const referenceModalFooter = (
        <div className="custom_modal_footer">
            <Button
                loading={addLoading}
                label={`${addLoading ? 'Processing' : selectedNoteDefault ? 'Add' : `Add selected notes (${selectedNotes?.length})`}`}
                className="custom_btn primary"
                onClick={selectedNoteDefault ? handleAddSingleNote : handleAddMultipleNotes}
                disabled={selectedNotes?.length === 0}
            />
        </div>
    )
    /*  */


    /* SOW */
    useEffect(() => {
        if (sowViewOption === "Simple") {
            if (!activeSimpleItem && quotationDetail?.sow_data?.simple_data?.simple_data_list?.length) {
                setActiveSimpleItem(quotationDetail.sow_data.simple_data.simple_data_list[0]);
            } else if (activeSimpleItem) {
                const found = quotationDetail?.sow_data?.simple_data?.simple_data_list?.find(
                    item => item.id === activeSimpleItem.id
                );
                if (found) setActiveSimpleItem(found);
            }
        } else if (sowViewOption === "Detail") {
            if (!activeDetailItem && quotationDetail?.sow_data?.detail_data?.detail_data_list?.length) {
                setActiveDetailItem(quotationDetail.sow_data.detail_data.detail_data_list[0]);
            } else if (activeDetailItem) {
                const found = quotationDetail?.sow_data?.detail_data?.detail_data_list?.find(
                    item => item.id === activeDetailItem.id
                );
                if (found) setActiveDetailItem(found);
            }
        }
    }, [sowViewOption, quotationDetail]);

    const handleChangeSOWViewOption = (state: 'Simple' | 'Detail') => {
        setSowViewOption(state);
        handleExitSimpleItemEdit(activeSimpleItem!);
        handleExitDetailItemEdit(activeDetailItem!);
    }

    const handlePreviewReport = () => {
        setShowReportConfigModal(true);
    }

    const handleCloseReportConfigModal = () => {
        setShowReportConfigModal(false);
    }

    const amountLabelBody = (amount: number) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {currencySymbol}&nbsp;{amount.toFixed(2)}
                </span>
            </div>
        )
    }

    const titleLabelBody = (title: string) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {title}
                </span>
            </div>
        )
    }

    const countLabelBody = (count: number) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {String(count).padStart(2, '0')}
                </span>
            </div>
        )
    }

    // Simple
    const handleToggleSimpleItem = (simpleItem: SOWSimpleDataList) => {
        setActiveSimpleItem((prevSelected) =>
            prevSelected?.id === simpleItem.id ? null : simpleItem
        );

        handleExitSimpleItemEdit(simpleItem);
        goToTop();
    };

    const handleCloseSimpleItemModal = () => {
        setShowSimpleItemModal(false);
        handleResetSimpleItemFields();
        handleResetSimpleItemErrors();
    }

    const handleResetSimpleItemFields = () => {
        setSimpleItemName('');
        setMaterial(0);
        setLabour(0);
        setTotal(0);
    }

    const handleResetSimpleItemErrors = () => {
        setSimpleItemNameError('');
        setMaterialError('');
        setLabourError('');
        setTotalError('');
    }

    const handleAddSimpleSubItem = (simpleItem: SOWSimpleDataList) => {
        setActiveSimpleItem(simpleItem);
        handleExitSimpleItemEdit(simpleItem);
        setDataState('Add');
        setShowSimpleItemModal(true);
    }


    // validation simple sub item
    function checkCredentialstSimpleSubItem(): boolean {
        const itemname = checkText(simpleItemName!, true, setSimpleItemNameError);
        return itemname;
    }

    useEffect(() => {
        checkText(simpleItemName!, false, setSimpleItemNameError);
    }, [simpleItemName]);


    // database Error set
    function showAPIErrorSimpleItem(keyName: string, error: string): void {
        if (keyName === "quotation_sub_item_name") {
            setSimpleItemNameError(error);
        }
    }


    // Simple sub item create api
    const handleCreateSimpleSubItem = async (e: React.FormEvent, addAnother: boolean) => {
        e.preventDefault();

        if (checkCredentialstSimpleSubItem()) {
            if (addAnother) {
                setAddMoreLoading(true);
            } else {
                setLoading(true);
            }
            const data = await createQuotationSubItemSimple(
                quotationDetail?.id!,
                activeSimpleItem?.id!,
                simpleItemName,
            );
            if (data.statusCode === 200) {
                await dispatch(fetchQuotationData());

                if (addAnother) {
                    setAddMoreLoading(false);
                    handleResetSimpleItemFields();
                    handleResetSimpleItemErrors();
                } else {
                    setLoading(false);
                    handleCloseSimpleItemModal();
                }

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Item added successfully.',
                        life: 3000
                    });
                }
            } else if (data.statusCode === 422) {
                if (addAnother) {
                    setAddMoreLoading(false);
                } else {
                    setLoading(false);
                }
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorSimpleItem(key, value[0])
                ));
            } else {
                if (addAnother) {
                    setAddMoreLoading(false);
                } else {
                    setLoading(false);
                }

                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding the item(s).',
                        life: 3000
                    });
                }
            }
        }
    }

    useEffect(() => {
        if (selectedSimpleSubItem) {
            if (simpleItemName !== selectedSimpleSubItem?.quotation_sub_item_name) {
                setsimpleSubItemUpdateHasChanges(true);
            } else if ((material === 0 ? null : material) !== selectedSimpleSubItem?.sub_item_simple_data?.material) {
                setsimpleSubItemUpdateHasChanges(true);
            } else if ((labour === 0 ? null : labour) !== selectedSimpleSubItem?.sub_item_simple_data?.labour) {
                setsimpleSubItemUpdateHasChanges(true);
            } else if ((total === 0 ? null : total) !== selectedSimpleSubItem?.sub_item_simple_data?.total) {
                setsimpleSubItemUpdateHasChanges(true);
            } else {
                setsimpleSubItemUpdateHasChanges(false);
            }
        }
    }, [selectedSimpleSubItem, simpleItemName, material, labour, total]);

    const handleEditSimpleSubItem = (simpleSubItem: SOWSimpleDataSubItemList) => {
        if (simpleSubItem) {
            handleExitSimpleItemEdit(activeSimpleItem!);
            setSelectedSimpleSubItem(simpleSubItem);
            setShowSimpleItemModal(true);
            setDataState('Edit');

            setSimpleItemName(simpleSubItem?.quotation_sub_item_name || '');
            setMaterial(simpleSubItem?.sub_item_simple_data?.material || 0);
            setLabour(simpleSubItem?.sub_item_simple_data?.labour || 0);
            setTotal(simpleSubItem?.sub_item_simple_data?.total || 0);

            if (simpleSubItem?.sub_item_simple_data?.material === null && simpleSubItem?.sub_item_simple_data?.labour === null && simpleSubItem?.sub_item_simple_data?.total === null) {
                setLabourDisabled(false);
                setMaterialDisabled(false);
                setTotalDisabled(false);
            } else if (simpleSubItem?.sub_item_simple_data?.material !== null || simpleSubItem?.sub_item_simple_data?.labour !== null) {
                setTotalDisabled(true);
                setLabourDisabled(false);
                setMaterialDisabled(false);
            } else {
                setLabourDisabled(true);
                setMaterialDisabled(true);
                setTotalDisabled(false);
            }
        }
    }

    // validation simple sub item
    function checkCredentialstSimpleSubItemDetail(): boolean {
        const itemname = checkText(simpleItemName!, true, setSimpleItemNameError);
        return itemname;
    }

    useEffect(() => {
        checkText(simpleItemName!, false, setSimpleItemNameError);
    }, [simpleItemName]);

    // database Error set
    function showAPIErrorSimpleItemDetail(keyName: string, error: string): void {
        if (keyName === "quotation_sub_item_name") {
            setSimpleItemNameError(error);
        }

        if (keyName === "material") {
            setMaterialCostError(error);
        }
        if (keyName === "labour") {
            setLabourCostError(error);
        }
        if (keyName === "total") {
            setTotalError(error);
        }
    }

    // Simple sub item update api
    const handleUpdateSimpleSubItem = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialstSimpleSubItemDetail()) {
            setLoading(true);
            const data = await createQuotationSubItemSimpleItemData(
                quotationDetail?.id!,
                selectedSimpleSubItem?.id!,
                simpleItemName,
                (materialDisabled === false) ? material : null,
                (labourDisabled === false) ? labour : null,
                (totalDisabled === false) ? total : null
            );
            if (data.statusCode === 200) {
                setLoading(false);
                handleCloseSimpleItemModal();

                setLabourDisabled(false);
                setMaterialDisabled(false);
                setTotalDisabled(false);

                // const updatedValues: EditingValues = {
                //     material: materialDisabled ? null : material,
                //     labour: labourDisabled ? null : labour,
                //     total: totalDisabled ? null : total,
                // };

                // setEditingSimpleSubItemValues((prev) => {
                //     const next = { ...prev, [selectedSimpleSubItem!.id]: updatedValues };

                //     editingSimpleValuesRef.current = next;
                //     return next;
                // });

                dispatch(fetchQuotationData());

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Item updated successfully.',
                        life: 3000
                    });
                }
            } else if (data.statusCode === 422) {
                setLoading(false);
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorSimpleItemDetail(key, value[0])
                ));
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the item.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleDeleteSimpleSubItem = (simpleSubItem: SOWSimpleDataSubItemList) => {
        const simpleSubItemId = simpleSubItem?.id;

        confirmDialog({
            message: 'Are you sure you want to delete this item details?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteSimpleSubItem(simpleSubItemId),
        });
    }

    // Simple sub item delete api
    const deleteSimpleSubItem = async (simpleSubItemId: number) => {
        const result = await deleteQuotationSubItem(
            quotationDetail?.id!,
            simpleSubItemId);

        if (result.statusCode === 200) {
            dispatch(fetchQuotationData());
            handleCloseNoteModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Item details deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the item details.',
                    life: 3000
                });
            }
        }
    }

    const simpleItemModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} {activeSimpleItem?.quotation_item_name} sub item
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseSimpleItemModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const simpleItemModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseSimpleItemModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={(e) =>
                    dataState === 'Add'
                        ? handleCreateSimpleSubItem(e, false)
                        : handleUpdateSimpleSubItem(e)
                }
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !simpleSubItemUpdateHasChanges}
            />

            {dataState === 'Add' && (
                <Button
                    label={addMoreLoading ? 'Processing' : 'Next'}
                    onClick={(e) => handleCreateSimpleSubItem(e, true)}
                    loading={addMoreLoading}
                    className="custom_btn primary"
                />
            )}
        </div>
    );

    const handleMaterialChange = (e: InputNumberChangeEvent) => {
        const newMaterial = e.value ?? 0;
        setMaterial(newMaterial);

        if (newMaterial > 0 || (labour ?? 0) > 0) {
            setTotal(newMaterial + (labour ?? 0));
            setTotalDisabled(true); // disable total
        } else {
            setTotal(0);
            setTotalDisabled(false); // enable total
        }
    };

    const handleLabourChange = (e: InputNumberChangeEvent) => {
        const newLabour = e.value ?? 0;
        setLabour(newLabour);

        if ((material ?? 0) > 0 || newLabour > 0) {
            setTotal((material ?? 0) + newLabour);
            setTotalDisabled(true); // disable total
        } else {
            setTotal(0);
            setTotalDisabled(false); // enable total
        }
    };

    const handleTotalChange = (e: InputNumberChangeEvent) => {
        const newTotal = e.value ?? 0;
        setTotal(newTotal);

        if (newTotal > 0) {
            setMaterialDisabled(true);
            setLabourDisabled(true);
        } else {
            setMaterialDisabled(false);
            setLabourDisabled(false);
        }
    };

    /*  */
    // helpers
    const normalizeEditingSimpleSubItemValues = (
        material: number | null,
        labour: number | null,
        total: number | null
    ): SimpleEditingValues => {
        if ((material ?? 0) > 0 || (labour ?? 0) > 0) {
            return { material: material ?? 0, labour: labour ?? 0, total: null };
        }
        if ((total ?? 0) > 0) {
            return { material: null, labour: null, total: total ?? 0 };
        }
        return { material: null, labour: null, total: null };
    };

    const normalizeForApi = (value: number | null | undefined): number => {
        if (value === null || value === undefined || isNaN(value)) return 0;
        return Number(value);
    };

    const isMaterialLabourDisabled = (values: SimpleEditingValues) =>
        (values.total ?? 0) > 0;

    const isTotalDisabled = (values: SimpleEditingValues) =>
        (values.material ?? 0) > 0 || (values.labour ?? 0) > 0;

    const handleToggleEditSimpleSubItems = (simpleItem: SOWSimpleDataList) => {
        if (editableSimpleItemId === simpleItem.id) {
            setEditableSimpleItemId(null);
            const next = { ...editingSimpleSubItemValues };
            simpleItem.sub_item_list?.forEach((s: SOWSimpleDataSubItemList) => {
                delete next[s.id];
            });
            setEditingSimpleSubItemValues(next);
            editingSimpleValuesRef.current = next;
        } else {
            setActiveSimpleItem(simpleItem);
            setEditableSimpleItemId(simpleItem.id);

            const init: Record<number, SimpleEditingValues> = {};
            simpleItem.sub_item_list?.forEach((s: SOWSimpleDataSubItemList) => {
                init[s.id] = {
                    material: s?.sub_item_simple_data?.material ?? null,
                    labour: s?.sub_item_simple_data?.labour ?? null,
                    total: s?.sub_item_simple_data?.total ?? null,
                };
            });

            const merged = { ...editingSimpleSubItemValues, ...init };
            setEditingSimpleSubItemValues(merged);
            editingSimpleValuesRef.current = merged;
        }
    };

    const handleExitSimpleItemEdit = (simpleItem: SOWSimpleDataList) => {
        if (!simpleItem) {
            return;
        }

        setEditableSimpleItemId(null);
        const next = { ...editingSimpleSubItemValues };
        simpleItem.sub_item_list?.forEach((s: SOWSimpleDataSubItemList) => {
            delete next[s.id];
        });
        setEditingSimpleSubItemValues(next);
        editingSimpleValuesRef.current = next;
    };

    const handleSimpleSubItemValueChange = (
        subItemId: number,
        field: "material" | "labour" | "total",
        value: number | null
    ) => {
        setEditingSimpleSubItemValues((prev) => {
            const old = prev[subItemId] ?? {
                material: null,
                labour: null,
                total: null,
            };

            const raw = {
                material: field === "material" ? value ?? 0 : old.material ?? 0,
                labour: field === "labour" ? value ?? 0 : old.labour ?? 0,
                total: field === "total" ? value ?? 0 : old.total ?? 0,
            };

            const newVal = normalizeEditingSimpleSubItemValues(raw.material, raw.labour, raw.total);
            const next = { ...prev, [subItemId]: newVal };
            editingSimpleValuesRef.current = next;
            return next;
        });
    };

    const updateSimpleSubItem = async (
        subItem: SOWSimpleDataSubItemList,
        field: "material" | "labour" | "total"
    ) => {
        if (!editableSimpleItemId) return;

        const fromRef = editingSimpleValuesRef.current[subItem.id] ?? {
            material: subItem?.sub_item_simple_data?.material,
            labour: subItem?.sub_item_simple_data?.labour,
            total: subItem?.sub_item_simple_data?.total,
        };

        let mat = fromRef.material ?? null;
        let lab = fromRef.labour ?? null;
        let tot = fromRef.total ?? null;

        if (tot && tot > 0) {
            mat = null;
            lab = null;
        } else if ((mat && mat > 0) || (lab && lab > 0)) {
            tot = null;
        } else {
            mat = null;
            lab = null;
            tot = null;
        }

        try {
            setLoadingSimpleSubItemId(subItem.id);
            setLoadingSimpleField(field);

            const resp = await updateQuotationSubItemSimpleItemData(
                quotationDetail?.id!,
                subItem.id,
                mat,
                lab,
                tot
            );

            if (resp?.statusCode === 200) {
                const saved: SimpleEditingValues = { material: mat, labour: lab, total: tot };

                setEditingSimpleSubItemValues((prev) => {
                    const next = { ...prev, [subItem.id]: saved };
                    editingSimpleValuesRef.current = next;
                    return next;
                });

                dispatch(fetchQuotationData());

                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Item updated successfully.",
                    life: 3000,
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error updating the item.",
                    life: 3000,
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to update item.",
                life: 3000,
            });
        } finally {
            setLoadingSimpleSubItemId(null);
            setLoadingSimpleField(null);
        }
    };

    const simpleItemActionBody = (simpleItem: SOWSimpleDataList) => {
        const isEditing = editableSimpleItemId === simpleItem.id;

        return (
            <div className="table_action_btn_area justify-content-end pe-sm-3 mt-1 mb-1">
                {(canEdit) && (
                    <button
                        className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleToggleEditSimpleSubItems(simpleItem);
                            e.stopPropagation();
                        }}>
                        {isEditing ? 'Exit' : 'Edit'}
                        <Ripple />
                    </button>
                )}

                {(canEdit) && (
                    <button
                        className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleAddSimpleSubItem(simpleItem);
                            e.stopPropagation();
                        }}>
                        Add
                        <Ripple />
                    </button>
                )}
            </div>
        )
    }

    const simpleSubItemActionBody = (simpleSubItem: SOWSimpleDataSubItemList) => {
        return (
            <div className="table_action_btn_area sub justify-content-end pe-sm-3">
                {(canEdit) && (
                    <React.Fragment>
                        {editableSimpleItemId !== activeSimpleItem?.id && (
                            <button
                                className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
                                onClick={() => handleEditSimpleSubItem(simpleSubItem)}
                            >
                                <i className="bi bi-pencil-square"></i>
                                <Ripple />
                            </button>
                        )}

                        <button
                            className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
                            onClick={() => handleDeleteSimpleSubItem(simpleSubItem)}
                        >
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    </React.Fragment>
                )}
            </div>
        )
    }

    /* Detail */
    const handleToggleDetailItem = (detailItem: SOWDetailDataList) => {
        setActiveDetailItem((prevSelected) =>
            prevSelected?.id === detailItem.id ? null : detailItem
        );

        handleExitDetailItemEdit(detailItem);
        goToTop();
    };

    const handleCloseDetailItemModal = () => {
        setShowDetailItemModal(false);
        handleResetDetailItemFields();
        handleResetDetailItemErrors();
    }

    const handleResetDetailItemFields = () => {
        setDetailItemName('');
        setQuantity(0);
        setUnit(null);
        setUnitPrice(0);
        setMaterialCost(0);
        setLabourCost(0);
    }

    const handleResetDetailItemErrors = () => {
        setDetailItemNameError('');
        setQuantityError('');
        setUnitError('');
        setUnitPriceError('');
        setMaterialCostError('');
        setLabourCostError('');
    }

    const handleAddDetailSubItem = (detailItem: SOWDetailDataList) => {
        setActiveDetailItem(detailItem);
        handleExitDetailItemEdit(detailItem);

        setDataState('Add');
        setShowDetailItemModal(true);
    }

    // validation details sub item

    // validation simple sub item
    function checkCredentialstDetailSubItem(): boolean {
        const itemname = checkText(detailItemName!, true, setDetailItemNameError);
        const up = checkNumber(unitPrice!, true, setUnitPriceError);
        const qty = checkNumber(quantity!, true, setQuantityError);
        const unitx = checkModelSelected((unit != null), true, setUnitError)
        return itemname && unitx && up && qty;
    }

    useEffect(() => {
        checkText(detailItemName!, false, setDetailItemNameError);
    }, [detailItemName]);

    useEffect(() => {
        checkNumber(labourCost, false, setLabourCostError);
    }, [labourCost]);


    useEffect(() => {
        checkNumber(quantity, false, setQuantityError);
    }, [quantity]);

    useEffect(() => {
        checkNumber(unitPrice, false, setUnitPriceError);
    }, [unitPrice]);

    useEffect(() => {
        checkModelSelected((unit != null), false, setUnitError);
    }, [unit]);



    // database Error set
    function showAPIErrorDetailItem(keyName: string, error: string): void {
        if (keyName === "quotation_sub_item_name") {
            setSimpleItemNameError(error);
        }

        if (keyName === "unit_id") {
            setUnitError(error);
        }

        if (keyName === "quantity") {
            setQuantityError(error);
        }

        if (keyName === "unit_price") {
            setUnitPriceError(error);
        }

        if (keyName === "labour_cost") {
            setLabourCostError(error);
        }
    }

    // Detail sub item create api
    const handleCreateDetailSubItem = async (e: React.FormEvent, addAnother: boolean) => {
        e.preventDefault();

        if (checkCredentialstDetailSubItem()) {
            if (addAnother) {
                setAddMoreLoading(true);
            } else {
                setLoading(true);
            }
            const data = await createQuotationSubItemDetail(
                quotationDetail?.id!,
                activeDetailItem?.id!,
                detailItemName,
                unit?.id!,
                quantity,
                unitPrice,
                labourCost
            );
            if (data.statusCode === 200) {
                await dispatch(fetchQuotationData());

                if (addAnother) {
                    setAddMoreLoading(false);
                    handleResetDetailItemFields();
                    handleResetDetailItemErrors();
                } else {
                    setLoading(false);
                    handleCloseDetailItemModal();
                }

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Item added successfully.',
                        life: 3000
                    });
                }
            } else if (data.statusCode === 422) {
                if (addAnother) {
                    setAddMoreLoading(false);
                } else {
                    setLoading(false);
                }
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorDetailItem(key, value[0])
                ));
            } else {
                if (addAnother) {
                    setAddMoreLoading(false);
                } else {
                    setLoading(false);
                }

                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding the item(s).',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleEditDetailSubItem = (detailSubItem: SOWDetailDataSubItemList) => {
        if (detailSubItem) {
            handleExitDetailItemEdit(activeDetailItem!);
            setSelectedDetailSubItem(detailSubItem);
            setShowDetailItemModal(true);
            setDataState('Edit');

            setDetailItemName(detailSubItem?.quotation_sub_item_name || '');
            setUnit(detailSubItem?.sub_item_detail_data?.unit || null);
            setQuantity(detailSubItem?.sub_item_detail_data?.quantity || 0);
            setUnitPrice(detailSubItem?.sub_item_detail_data?.unit_price || 0);
            setMaterialCost(detailSubItem?.sub_item_detail_data?.material_cost || 0);
            setLabourCost(detailSubItem?.sub_item_detail_data?.labour_cost || 0);
        }
    }

    // Detail sub item update api
    const handleUpdateDetailSubItem = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialstDetailSubItem()) {
            setLoading(true);

            const data = await createQuotationSubItemDetailAdd(
                quotationDetail?.id!,
                selectedDetailSubItem?.id!,
                detailItemName,
                unit?.id!,
                quantity,
                unitPrice,
                labourCost
            );
            if (data.statusCode === 200) {
                await dispatch(fetchQuotationData());
                setLoading(false);
                handleCloseDetailItemModal();

                dispatch(fetchQuotationData);

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Item updated successfully.',
                        life: 3000
                    });
                }

            } else {
                setLoading(false);

                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the item.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleDeleteDetailSubItem = (detailSubItem: SOWDetailDataSubItemList) => {
        const detailSubItemId = detailSubItem?.id;

        confirmDialog({
            message: 'Are you sure you want to delete this item details?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteDetailSubItem(detailSubItemId),
        });
    }

    // Detail sub item delete api
    const deleteDetailSubItem = async (detailSubItemId: number) => {
        const result = await deleteQuotationSubItem(
            quotationDetail?.id!,
            detailSubItemId);

        if (result.statusCode === 200) {
            dispatch(fetchQuotationData());
            handleCloseNoteModal();
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Item details deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the item details.',
                    life: 3000
                });
            }
        }
    }

    const detailItemModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} {activeDetailItem?.quotation_item_name} sub item
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseDetailItemModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const detailItemModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDetailItemModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={(e) =>
                    dataState === 'Add'
                        ? handleCreateDetailSubItem(e, false)
                        : handleUpdateDetailSubItem(e)
                }
                loading={loading}
                className="custom_btn primary"
            />

            {dataState === 'Add' && (
                <Button
                    label={addMoreLoading ? 'Processing' : 'Next'}
                    onClick={(e) => handleCreateDetailSubItem(e, true)}
                    loading={addMoreLoading}
                    className="custom_btn primary"
                />
            )}
        </div>
    );

    const handleQuantityChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setQuantity(e.value);

            if (unitPrice !== null) {
                setMaterialCost(e.value * unitPrice)
            } else {
                setMaterialCost(0);
            }
        } else {
            setQuantity(0);
            setMaterialCost(0);
        }
    };

    const handleUnitPriceChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setUnitPrice(e.value);

            if (quantity !== null) {
                setMaterialCost(quantity * e.value)
            } else {
                setMaterialCost(0);
            }
        } else {
            setUnitPrice(0);
            setMaterialCost(0);
        }
    };

    const handleMaterialCostChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setMaterialCost(e.value);
        } else {
            setMaterialCost(0);
        }
    };

    const handleLabourCostChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setLabourCost(e.value);
        } else {
            setLabourCost(0);
        }
    };

    /*  */
    const handleToggleEditDetailSubItems = (detailItem: SOWDetailDataList) => {
        if (editableDetailItemId === detailItem.id) {
            setEditableDetailItemId(null);
            const next = { ...editingDetailSubItemValues };
            detailItem.sub_item_list?.forEach((s: SOWDetailDataSubItemList) => {
                delete next[s.id];
            });
            setEditingDetailSubItemValues(next);
            editingDetailValuesRef.current = next;
        } else {
            setActiveDetailItem(detailItem);
            setEditableDetailItemId(detailItem.id);

            const init: Record<number, DetailEditingValues> = {};
            detailItem.sub_item_list?.forEach((s: SOWDetailDataSubItemList) => {
                init[s.id] = {
                    quantity: s?.sub_item_detail_data?.quantity ?? null,
                    unitPrice: s?.sub_item_detail_data?.unit_price ?? null,
                    labour: s?.sub_item_detail_data?.labour_cost ?? null,
                };
            });

            const merged = { ...editingDetailSubItemValues, ...init };
            setEditingDetailSubItemValues(merged);
            editingDetailValuesRef.current = merged;
        }
    };

    const handleExitDetailItemEdit = (detailItem: SOWDetailDataList | null) => {
        if (!detailItem) {
            return;
        }

        setEditableDetailItemId(null);
        const next = { ...editingDetailSubItemValues };

        detailItem.sub_item_list?.forEach((s: SOWDetailDataSubItemList) => {
            delete next[s.id];
        });

        setEditingDetailSubItemValues(next);
        editingDetailValuesRef.current = next;
    };


    const normalizeEditingDetailSubItemValues = (
        quantity: number | null,
        unitPrice: number | null,
        labour: number | null,
    ): DetailEditingValues => {
        return { quantity, unitPrice, labour };
    };

    const handleDetailSubItemValueChange = (
        subItemId: number,
        field: "labour" | "quantity" | "unit price",
        value: number | null
    ) => {
        setEditingDetailSubItemValues((prev) => {
            const old = prev[subItemId] ?? {
                quantity: null,
                unitPrice: null,
                labour: null,
            };

            const raw = {
                quantity: field === "quantity" ? value : old.quantity,
                unitPrice: field === "unit price" ? value : old.unitPrice,
                labour: field === "labour" ? value : old.labour,
            };

            const newVal = normalizeEditingDetailSubItemValues(
                raw.quantity,
                raw.unitPrice,
                raw.labour
            );

            const next = { ...prev, [subItemId]: newVal };
            editingDetailValuesRef.current = next;
            return next;
        });
    };

    const updateDetailSubItem = async (
        subItem: SOWDetailDataSubItemList,
        field: "labour" | "quantity" | "unit price",
    ) => {
        if (!editableDetailItemId) return;

        const fromRef = editingDetailValuesRef.current[subItem.id] ?? {
            quantity: subItem?.sub_item_detail_data?.quantity,
            unitPrice: subItem?.sub_item_detail_data?.unit_price,
            labour: subItem?.sub_item_detail_data?.labour_cost,
        };

        const qty = fromRef.quantity;
        const up = fromRef.unitPrice;
        const lab = fromRef.labour;

        try {
            setLoadingDetailSubItemId(subItem.id);
            setLoadingDetailField(field);

            const resp = await createQuotationSubItemDetailEdit(
                quotationDetail?.id!,
                subItem.id,
                qty,
                up,
                lab,
            );

            if (resp?.statusCode === 200) {
                const saved: DetailEditingValues = { quantity: qty, unitPrice: up, labour: lab };

                setEditingDetailSubItemValues((prev) => {
                    const next = { ...prev, [subItem.id]: saved };
                    editingDetailValuesRef.current = next;
                    return next;
                });

                dispatch(fetchQuotationData());

                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Item updated successfully.",
                    life: 3000,
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error updating the item.",
                    life: 3000,
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to update item.",
                life: 3000,
            });
        } finally {
            setLoadingDetailSubItemId(null);
            setLoadingDetailField(null);
        }
    };
    /* */

    const detailItemActionBody = (detailItem: SOWDetailDataList) => {
        const isEditing = editableDetailItemId === detailItem.id;

        return (
            <div className="table_action_btn_area justify-content-end pe-sm-3 mt-1 mb-1">
                {(canEdit) && (
                    <button
                        className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleToggleEditDetailSubItems(detailItem);
                            e.stopPropagation();
                        }}>
                        {isEditing ? 'Exit' : 'Edit'}
                        <Ripple />
                    </button>
                )}

                {(canEdit) && (
                    <button
                        className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleAddDetailSubItem(detailItem);
                            e.stopPropagation();
                        }}>
                        Add
                        <Ripple />
                    </button>
                )}
            </div>
        )
    }

    const detailSubItemActionBody = (detailSubItem: SOWDetailDataSubItemList) => {
        return (
            <div className="table_action_btn_area sub justify-content-end pe-sm-3">
                {(canEdit) && (<React.Fragment>
                    {editableDetailItemId !== activeDetailItem?.id && (
                        <button
                            className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
                            onClick={() => handleEditDetailSubItem(detailSubItem)}
                        >
                            <i className="bi bi-pencil-square"></i>
                            <Ripple />
                        </button>
                    )}

                    <button
                        className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
                        onClick={() => handleDeleteDetailSubItem(detailSubItem)}
                    >
                        <i className="bi bi-trash3"></i>
                        <Ripple />
                    </button>
                </React.Fragment>)}
            </div>
        )
    }
    /*  */


    /* Terms */
    const handleCloseTermsModal = () => {
        setShowTermsModal(false);
        handleResetTermsFields();
        handleResetTermsErrors();
    }

    const handleResetTermsFields = () => {
        setAdvancePayment(0);
        setStartDate(null);
        setDurationOption('Week');
        setDuration(0);
    }

    const handleResetTermsErrors = () => {
        setAdvancePaymentError('');
        setStartDateError('');
        setDurationError('');
    }

    const handleAddTerm = (state: 'Advance payment' | 'Start date' | 'Duration') => {
        setTermsAddState(state);
        setShowTermsModal(true);
        setDataState('Add');
    }


    function checkCredentialstTerms(): boolean {
        // const advance = checkNumber(advancePayment!, true, setAdvancePaymentError);
        // const dur = checkNumber(duration, true, setDurationError);
        // return advance || dur;
        return true;
    }

    useEffect(() => {
        checkNumber(advancePayment!, false, setAdvancePaymentError);
    }, [advancePayment]);


    useEffect(() => {
        checkNumber(duration, false, setDurationError);
    }, [duration]);

    // Terms detail save api
    const handleCreateTerm = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialstTerms()) {
            setLoading(true);
            const data = await createQuotationTerms(
                quotationDetail?.id!,
                advancePayment!,
                startDate != null ? formatDate(startDate!) : null,
                durationOption,
                duration
            );

            if (data.statusCode === 200) {
                setLoading(false);
                handleCloseTermsModal();
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Term detail added successfully.',
                        life: 3000
                    });
                }
            } else {
                setLoading(false);

                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding the term detail.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleEditTerm = (state: 'Advance payment' | 'Start date' | 'Duration') => {
        setTermsAddState(state);
        setShowTermsModal(true);
        setDataState('Edit');

        if (state === 'Advance payment') {
            setAdvancePayment(quotationDetail?.term_data?.advance_payment || 0);
        } else if (state === 'Start date') {
            setStartDate(quotationDetail?.term_data?.start_date ? parseStringToDate(quotationDetail?.term_data?.start_date) : null);
        } else if (state === 'Duration') {
            setDurationOption(quotationDetail?.term_data?.duration_type!);
            setDuration(quotationDetail?.term_data?.duration || 0);
        }
    }

    // Terms detail update api
    const handleUpdateTerm = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialstTerms()) {
            setLoading(true);
            const data = await createQuotationTerms(
                quotationDetail?.id!,
                advancePayment!,
                formatDate(startDate!),
                durationOption,
                duration
            );
            if (data.statusCode === 200) {
                setLoading(false);
                handleCloseTermsModal();
                dispatch(fetchQuotationData());

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Term detail updated successfully.',
                        life: 3000
                    });
                }
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the term detail.',
                        life: 3000
                    });
                }
            }
        }
    }

    const termModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} {termsAddState}
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseTermsModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const termModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseTermsModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateTerm : handleUpdateTerm}
                loading={loading} className="custom_btn primary"
            />
        </div>
    );

    const handleAdvancePaymentChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setAdvancePayment(e.value);
        } else {
            setAdvancePayment(0);
        }
    };

    const handleDurationChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setDuration(e.value);
        } else {
            setDuration(0);
        }
    };
    /*  */

    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };

    const handleToggleSideBar = () => {
        setShowSideBar(!showSideBar);
    }
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            {((loadingQuotation || loadingQuotationNoteList) && initalLoad) ? (
                <div className="loading_container">
                    <ProgressSpinner
                        className="progresscircle custom"
                        style={{ width: '50px', height: '50px', stroke: '#8953D7' }}
                        strokeWidth="8"
                        fill="var(--surface-ground)"
                        animationDuration="1s"
                    />
                </div>
            ) : errorQuotation ? (
                <ErrorPage errorMessage={errorQuotation} containerClassname="full_height" />
            ) : (
                <React.Fragment>
                    <div className="project_toolbar">
                        <Button
                            icon={`bi ${showSideBar ? 'bi-arrow-left' : 'bi-list'}`}
                            onClick={handleToggleSideBar} className="sidebar_toggle_btn"
                        />

                        <div className="project_tool_options">
                            <div className={`project_tool_options_sub`}>
                                {quotationDetailViewState === 'Cover page' && (
                                    <button className={`is_btn project_tool_btn ms-3 ms-sm-5 ms-lg-0 p-ripple`}
                                        onClick={handleViewCoverPagePreview}
                                        disabled={coverPageData === null}>
                                        Preview
                                        <Ripple />
                                    </button>
                                )}
                            </div>

                            <div className={`project_tool_options_sub `}>
                                {quotationDetailViewState === 'SOW' && (
                                    <button
                                        className="is_btn project_tool_btn p-ripple"
                                        onClick={handlePreviewReport}>
                                        Report
                                        <Ripple />
                                    </button>
                                )}

                                <button className={`is_btn project_tool_btn p-ripple`}
                                    onClick={goBack}>
                                    Back
                                    <Ripple />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={`project_sidebar ${showSideBar ? 'show' : ''}`}>
                        <ul className="section_list">
                            {/* Cover page */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Cover page' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Cover page')}>
                                        <span>Cover page</span>
                                    </button>
                                </div>
                            </li>

                            {/* Note */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Note' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Note')}>
                                        <span>Note</span>
                                    </button>
                                </div>
                            </li>

                            {/* SOW */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'SOW' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('SOW')}>
                                        <span>SOW</span>
                                    </button>
                                </div>
                            </li>

                            {/* Terms */}
                            <li className="section_list_item">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Terms' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Terms')}>
                                        <span>Terms</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className={`layout_backdrop ${showSideBar ? 'show' : ''}`} onClick={handleToggleSideBar}></div>

                    <div className={`project_main_content ${showSideBar ? 'has_sidebar' : ''}`}>
                        <div className="page_breadcrumb_area mb-3">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    Quotation
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Quotation {quotationNo ? String(quotationNo).padStart(2, '0') : ''}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {quotationDetail ? quotationDetail?.quotation_no : ''}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {quotationDetailViewState}
                                </li>
                            </ul>
                        </div>

                        {quotationDetailViewState === 'Cover page' ? (
                            <React.Fragment>
                                <article className="main_section_area">
                                    <div className="cover_page_container">
                                        <div className="row">
                                            <div className="col-12 col-xl-3 mx-auto page_divider">
                                                <div className="form_left_area">
                                                    <div className="form_img_upload_area">
                                                        <div className="form_img_upload">
                                                            <input
                                                                disabled={canEdit === false}
                                                                type="file"
                                                                accept={imageFileExtension}
                                                                className="form_img_upload_input"
                                                                id="companyLogo"
                                                                onChange={handleUploadCompanyLogo}
                                                            />
                                                            <label
                                                                htmlFor="companyLogo"
                                                                className={`form_img_upload_label p-ripple ${companyLogo ? 'uploaded' : ''}`}
                                                                style={{ backgroundImage: companyLogo ? `url(${companyLogo})` : `` }}
                                                            >
                                                                {!companyLogo && (
                                                                    <div className="upload_icon">
                                                                        <i className="bi bi bi-image"></i>
                                                                    </div>
                                                                )}
                                                                <Ripple />
                                                            </label>
                                                            {companyLogo ? (
                                                                <React.Fragment>
                                                                    {(haveCoverPageDetails && !coverPageUpdateHasChanges) ? (
                                                                        <label htmlFor="companyLogo" className="add_image_label mt-2">Change</label>
                                                                    ) : (
                                                                        <button className="upload_clear_button mt-2" onClick={handleClearCompanyLogo}>Clear</button>
                                                                    )}
                                                                </React.Fragment>
                                                            ) : (
                                                                <label htmlFor="companyLogo" className="add_image_label mt-2">Company Logo</label>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {companyLogoError &&
                                                        <small className="form_error_msg text-center mt-2">{companyLogoError}</small>
                                                    }
                                                </div>
                                            </div>

                                            <div className="col-12 col-xl-9 mx-auto">
                                                <div className="form_right_area">
                                                    <div className="page_data_form_area">
                                                        <div className="row">
                                                            {/* Company name */}
                                                            <div className="col-12">
                                                                <TextInput
                                                                    inputDisabled={canEdit === false}
                                                                    id="companyName"
                                                                    label="Company name"
                                                                    labelHtmlFor="companyName"
                                                                    required={true}
                                                                    value={companyName}
                                                                    placeholder="Enter company name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
                                                                    error={companyNameError}
                                                                />
                                                            </div>

                                                            {/* Project description */}
                                                            <div className="col-12">
                                                                <Textarea
                                                                    inputDisabled={canEdit === false}
                                                                    id="projectDescription"
                                                                    label="Project description"
                                                                    labelHtmlFor="projectDescription"
                                                                    required={false}
                                                                    value={projectDescription}
                                                                    placeholder="Enter description"
                                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProjectDescription(e.target.value)}
                                                                    error={projectDescriptionError}
                                                                />
                                                            </div>

                                                            {/* Project address */}
                                                            <div className="col-12">
                                                                <Textarea
                                                                    inputDisabled={canEdit === false}
                                                                    id="projectAddress"
                                                                    label="Project address"
                                                                    labelHtmlFor="projectAddress"
                                                                    required={true}
                                                                    value={projectAddress}
                                                                    placeholder="Enter address"
                                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProjectAddress(e.target.value)}
                                                                    error={projectAddressError}
                                                                />
                                                            </div>

                                                            {/* Client name */}
                                                            <div className="col-12">
                                                                <TextInput
                                                                    inputDisabled={canEdit === false}
                                                                    id="clientName"
                                                                    label="Client name"
                                                                    labelHtmlFor="clientName"
                                                                    required={true}
                                                                    value={clientName}
                                                                    placeholder="Enter client name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientName(e.target.value)}
                                                                    error={clientNameError}
                                                                />
                                                            </div>

                                                            {/* Save */}
                                                            <div className="col-12">
                                                                <div className="form_btn_area mb-0 justify-content-end">
                                                                    {(canEdit) && (<Button
                                                                        label={`${coverPageLoading ? 'Processing' : haveCoverPageDetails ? 'Update' : 'Save'}`}
                                                                        className="form_btn"
                                                                        loading={coverPageLoading}
                                                                        onClick={handleSaveCoverPageDetails}
                                                                        disabled={haveCoverPageDetails && !coverPageUpdateHasChanges}
                                                                    />)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </React.Fragment>
                        ) : quotationDetailViewState === 'Note' ? (
                            <React.Fragment>
                                {(canEdit) && (<div className="page_header_area justify-content-end mb-3">
                                    <div className="page_action_area">
                                        <button
                                            className="add_data_link_btn p-ripple"
                                            onClick={handleAddNote}>
                                            <i className="bi bi-plus-circle"></i>
                                            <span>Add note</span>
                                            <Ripple />
                                        </button>
                                    </div>
                                </div>)}

                                <article className="main_section_area p-2 p-sm-3">
                                    {quotationDetail?.note_list && quotationDetail?.note_list?.length > 0 ? (
                                        <React.Fragment>
                                            {quotationDetail?.note_list?.map((noteItem) => {
                                                return (
                                                    <article key={noteItem?.id} className="note_content_container">
                                                        <div className="note_content_area">
                                                            <p>{String(noteItem?.note_no).padStart(2, '0')}.&nbsp;</p>
                                                            <p>
                                                                {noteItem?.note}
                                                            </p>
                                                        </div>

                                                        {(canEdit) && (<div className="note_content_action_area">
                                                            <button
                                                                className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
                                                                onClick={() => handleEditNote(noteItem)}
                                                            >
                                                                <i className="bi bi-pencil-square"></i>
                                                                <Ripple />
                                                            </button>

                                                            <button
                                                                className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
                                                                onClick={() => handleDeleteNote(noteItem)}
                                                            >
                                                                <i className="bi bi-trash3"></i>
                                                                <Ripple />
                                                            </button>
                                                        </div>)}

                                                    </article>
                                                )
                                            })}
                                        </React.Fragment>
                                    ) : (
                                        <NoDataCreated
                                            message={`No notes have been added, click the Add button to add notes.`}
                                        />
                                    )}
                                </article>
                            </React.Fragment>
                        ) : quotationDetailViewState === 'SOW' ? (
                            <React.Fragment>
                                <div className="page_header_area mb-3">
                                    <div className="contact_tabs">
                                        <div className="contact_tab_item">
                                            <button
                                                type="button"
                                                className={`contact_tab_btn fixed_min_width ${sowViewOption === "Simple" ? "active" : ""} p-ripple`}
                                                onClick={() => handleChangeSOWViewOption('Simple')}
                                            >
                                                Simple
                                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                            </button>
                                        </div>
                                        <div className="contact_tab_item">
                                            <button
                                                type="button"
                                                className={`contact_tab_btn fixed_min_width ${sowViewOption === "Detail" ? "active" : ""} p-ripple`}
                                                onClick={() => handleChangeSOWViewOption('Detail')}
                                            >
                                                Detail
                                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="page_action_area">
                                        <div className="display_label">
                                            <span className="display_label_text">Total :&nbsp;</span>

                                            {sowViewOption === 'Simple' ? (
                                                <span>
                                                    {currencySymbol}&nbsp;
                                                    {(quotationDetail?.sow_data?.simple_data?.simple_data_total ?? 0)?.toLocaleString("en-GB", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </span>
                                            ) : (
                                                <span>
                                                    {currencySymbol}&nbsp;
                                                    {(quotationDetail?.sow_data?.detail_data?.detail_data_total ?? 0)?.toLocaleString("en-GB", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <article className="main_section_area p-2">
                                    {sowViewOption === 'Simple' && (
                                        <div className="pck_track_list_area">
                                            <div className="table-responsive pck_track_list_table_area">
                                                <table className="table pck_track_list_table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                                            <th style={{ width: "24%" }} className="text_no_wrap item_title_column">
                                                                Item
                                                            </th>

                                                            <th style={{ width: "18%" }} className="text-center item_value_column text_no_wrap">
                                                                Material
                                                            </th>

                                                            <th style={{ width: "18%" }} className="text-center item_value_column text_no_wrap">
                                                                Labour
                                                            </th>

                                                            <th style={{ width: "18%" }} className="text-center item_value_column text_no_wrap">
                                                                Total
                                                            </th>

                                                            <th style={{ width: "18%" }} className="text-center item_value_column text_no_wrap"></th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {quotationDetail?.sow_data?.simple_data?.simple_data_list && quotationDetail?.sow_data?.simple_data?.simple_data_list?.length > 0 && (
                                                            <React.Fragment>
                                                                {quotationDetail?.sow_data?.simple_data?.simple_data_list?.map((simpleData) => {
                                                                    const isActive = simpleData?.id === activeSimpleItem?.id;

                                                                    return (
                                                                        <React.Fragment>
                                                                            <tr
                                                                                key={`simpleItem_${simpleData?.id}`}
                                                                                onClick={() => handleToggleSimpleItem(simpleData)}
                                                                                className={`part_row ${isActive ? "active" : ""}`}
                                                                            >
                                                                                <td
                                                                                    style={{ width: "4rem" }}
                                                                                    className="text-center toggle_column"
                                                                                >
                                                                                    <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "60%" }}
                                                                                    colSpan={3}
                                                                                    className="text_no_wrap text_semi_bold"
                                                                                >
                                                                                    {simpleData?.quotation_item_name}
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "18%" }}
                                                                                    className="text_no_wrap item_value_column"
                                                                                >
                                                                                    {amountLabelBody(simpleData?.item_total || 0)}
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "18%" }}
                                                                                    className="text_no_wrap item_value_column"
                                                                                >
                                                                                    {simpleItemActionBody(simpleData)}
                                                                                </td>
                                                                            </tr>

                                                                            <AnimatePresence initial={false}>
                                                                                {isActive && (
                                                                                    <tr className="part_item_row">
                                                                                        <td
                                                                                            colSpan={6}
                                                                                            className="p-0"
                                                                                            style={{ width: "100%" }}
                                                                                        >
                                                                                            <motion.div
                                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                                animate={{ height: "auto", opacity: 1 }}
                                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                                transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                                style={{ overflow: "hidden" }}
                                                                                            >
                                                                                                {simpleData?.sub_item_list && simpleData?.sub_item_list?.length > 0 ? (
                                                                                                    <table className="table pck_track_list_table">
                                                                                                        {simpleData?.sub_item_list?.map((simpleSubItem) => {
                                                                                                            const values = editingSimpleSubItemValues[simpleSubItem.id] ?? {
                                                                                                                material: simpleSubItem?.sub_item_simple_data?.material ?? null,
                                                                                                                labour: simpleSubItem?.sub_item_simple_data?.labour ?? null,
                                                                                                                total: simpleSubItem?.sub_item_simple_data?.total ?? null,
                                                                                                            };

                                                                                                            const isEditingSimpleItem = editableSimpleItemId === simpleData?.id;
                                                                                                            const isUpdatingSimpleItem = loadingSimpleSubItemId === simpleSubItem.id;

                                                                                                            return (
                                                                                                                <tr key={`simpleSubItem${simpleSubItem?.id}`} className="part_item_sub_row">
                                                                                                                    <td style={{ width: "4rem" }} className="toggle_column"></td>
                                                                                                                    <td
                                                                                                                        style={{ width: "24%" }}
                                                                                                                        className="text_semi_bold item_title_column"
                                                                                                                    >
                                                                                                                        <span className="highlight">
                                                                                                                            {simpleSubItem?.quotation_sub_item_name ? simpleSubItem?.quotation_sub_item_name : '---'}
                                                                                                                        </span>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "18%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={values.material ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleSimpleSubItemValueChange(simpleSubItem.id, "material", e.value ?? null)
                                                                                                                                }
                                                                                                                                onBlur={() => updateSimpleSubItem(simpleSubItem, 'material')}
                                                                                                                                prefix={currencySymbol}
                                                                                                                                className="table_input"
                                                                                                                                minFractionDigits={2}
                                                                                                                                maxFractionDigits={2}
                                                                                                                                placeholder="0.00"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingSimpleItem ||
                                                                                                                                    isMaterialLabourDisabled(values) ||
                                                                                                                                    isUpdatingSimpleItem ||
                                                                                                                                    loadingSimpleField === "material"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingSimpleItem && loadingSimpleField === "material" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "18%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={values.labour ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleSimpleSubItemValueChange(simpleSubItem.id, "labour", e.value ?? null)
                                                                                                                                }
                                                                                                                                onBlur={() => updateSimpleSubItem(simpleSubItem, 'labour')}
                                                                                                                                prefix={currencySymbol}
                                                                                                                                className="table_input"
                                                                                                                                minFractionDigits={2}
                                                                                                                                maxFractionDigits={2}
                                                                                                                                placeholder="0.00"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingSimpleItem ||
                                                                                                                                    isMaterialLabourDisabled(values) ||
                                                                                                                                    isUpdatingSimpleItem ||
                                                                                                                                    loadingSimpleField === "labour"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingSimpleItem && loadingSimpleField === "labour" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "18%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={isTotalDisabled(values) ? simpleSubItem?.final_total ?? 0 : values.total ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleSimpleSubItemValueChange(simpleSubItem.id, "total", e.value ?? 0)
                                                                                                                                }
                                                                                                                                onBlur={() => updateSimpleSubItem(simpleSubItem, 'total')}
                                                                                                                                prefix={currencySymbol}
                                                                                                                                className="table_input"
                                                                                                                                minFractionDigits={2}
                                                                                                                                maxFractionDigits={2}
                                                                                                                                placeholder="0.00"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingSimpleItem ||
                                                                                                                                    isTotalDisabled(values) ||
                                                                                                                                    isUpdatingSimpleItem ||
                                                                                                                                    loadingSimpleField === "total"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingSimpleItem && loadingSimpleField === "total" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "18%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        {simpleSubItemActionBody(simpleSubItem)}
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            )
                                                                                                        })}
                                                                                                    </table>
                                                                                                ) : (
                                                                                                    <table className="table pck_track_list_table">
                                                                                                        <tr className="part_item_sub_row">
                                                                                                            <td className="text-center empty_data_column" colSpan={6}>
                                                                                                                <i className="bi bi-exclamation-circle"></i>
                                                                                                                No items have been added, click the Add button to add an item.
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    </table>
                                                                                                )}
                                                                                            </motion.div>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </AnimatePresence>
                                                                        </React.Fragment>
                                                                    )
                                                                })}
                                                            </React.Fragment>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {sowViewOption === 'Detail' && (
                                        <div className="pck_track_list_area">
                                            <div className="table-responsive pck_track_list_table_area">
                                                <table className="table pck_track_list_table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                                            <th style={{ width: "15%" }} className="text_no_wrap item_title_column">
                                                                Item
                                                            </th>

                                                            <th style={{ width: "8%" }} className="text-center item_sub_value_column text_no_wrap">
                                                                Unit
                                                            </th>

                                                            <th style={{ width: "13%" }} className="text-center item_value_column text_no_wrap">
                                                                Price
                                                            </th>

                                                            <th style={{ width: "8%" }} className="text-center item_sub_value_column text_no_wrap">
                                                                QTY.
                                                            </th>

                                                            <th style={{ width: "13%" }} className="text-center item_value_column text_no_wrap">
                                                                Material
                                                            </th>

                                                            <th style={{ width: "13%" }} className="text-center item_value_column text_no_wrap">
                                                                Labour
                                                            </th>

                                                            <th style={{ width: "13%" }} className="text-center item_value_column text_no_wrap">
                                                                Total
                                                            </th>

                                                            <th style={{ width: "13%" }} className="text-center item_value_column text_no_wrap"></th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {quotationDetail?.sow_data?.detail_data?.detail_data_list && quotationDetail?.sow_data?.detail_data?.detail_data_list?.length > 0 && (
                                                            <React.Fragment>
                                                                {quotationDetail?.sow_data?.detail_data?.detail_data_list?.map((detailData) => {
                                                                    const isActive = detailData?.id === activeDetailItem?.id;

                                                                    return (
                                                                        <React.Fragment>
                                                                            <tr
                                                                                key={`detailItem_${detailData?.id}`}
                                                                                onClick={() => handleToggleDetailItem(detailData)}
                                                                                className={`part_row ${isActive ? "active" : ""}`}
                                                                            >
                                                                                <td
                                                                                    style={{ width: "4rem" }}
                                                                                    className="text-center toggle_column"
                                                                                >
                                                                                    <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "70%" }}
                                                                                    colSpan={6}
                                                                                    className="text_no_wrap text_semi_bold"
                                                                                >
                                                                                    {detailData?.quotation_item_name}
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "13%" }}
                                                                                    className="text_no_wrap item_value_column"
                                                                                >
                                                                                    {amountLabelBody(detailData?.item_total || 0)}
                                                                                </td>

                                                                                <td
                                                                                    style={{ width: "13%" }}
                                                                                    className="text_no_wrap item_value_column"
                                                                                >
                                                                                    {detailItemActionBody(detailData)}
                                                                                </td>
                                                                            </tr>

                                                                            <AnimatePresence initial={false}>
                                                                                {isActive && (
                                                                                    <tr className="part_item_row">
                                                                                        <td
                                                                                            colSpan={9}
                                                                                            className="p-0"
                                                                                            style={{ width: "100%" }}
                                                                                        >
                                                                                            <motion.div
                                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                                animate={{ height: "auto", opacity: 1 }}
                                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                                transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                                style={{ overflow: "hidden" }}
                                                                                            >
                                                                                                {detailData?.sub_item_list && detailData?.sub_item_list?.length > 0 ? (
                                                                                                    <table className="table pck_track_list_table">
                                                                                                        {detailData?.sub_item_list?.map((detailSubItem) => {
                                                                                                            const values = editingDetailSubItemValues[detailSubItem.id] ?? {
                                                                                                                quantity: detailSubItem?.sub_item_detail_data?.quantity ?? null,
                                                                                                                unitPrice: detailSubItem?.sub_item_detail_data?.unit_price ?? null,
                                                                                                                labour: detailSubItem?.sub_item_detail_data?.labour_cost ?? null,
                                                                                                            };

                                                                                                            const isEditingDetailItem = editableDetailItemId === detailData?.id;
                                                                                                            const isUpdatingDetailItem = loadingDetailSubItemId === detailSubItem.id;

                                                                                                            return (
                                                                                                                <tr key={`detailSubItem${detailSubItem?.id}`} className="part_item_sub_row">
                                                                                                                    <td style={{ width: "4rem" }} className="toggle_column"></td>
                                                                                                                    <td
                                                                                                                        style={{ width: "15%" }}
                                                                                                                        className="text_semi_bold item_title_column"
                                                                                                                    >
                                                                                                                        <span className="highlight">
                                                                                                                            {detailSubItem?.quotation_sub_item_name ? detailSubItem?.quotation_sub_item_name : '---'}
                                                                                                                        </span>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "8%" }}
                                                                                                                        className="text_no_wrap item_sub_value_column"
                                                                                                                    >
                                                                                                                        {titleLabelBody(detailSubItem?.sub_item_detail_data?.unit?.unit_name || '---')}
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "13%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={values.unitPrice ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleDetailSubItemValueChange(detailSubItem.id, "unit price", e.value ?? null)
                                                                                                                                }
                                                                                                                                onBlur={() => updateDetailSubItem(detailSubItem, 'unit price')}
                                                                                                                                prefix={currencySymbol}
                                                                                                                                className="table_input"
                                                                                                                                minFractionDigits={2}
                                                                                                                                maxFractionDigits={2}
                                                                                                                                placeholder="0.00"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingDetailItem ||
                                                                                                                                    isUpdatingDetailItem ||
                                                                                                                                    loadingDetailField === "unit price"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingDetailItem && loadingDetailField === "unit price" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "8%" }}
                                                                                                                        className="text_no_wrap item_sub_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={values.quantity ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleDetailSubItemValueChange(detailSubItem.id, "quantity", e.value ?? null)
                                                                                                                                }
                                                                                                                                onBlur={() => updateDetailSubItem(detailSubItem, 'quantity')}
                                                                                                                                className="table_input"
                                                                                                                                placeholder="0"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingDetailItem ||
                                                                                                                                    isUpdatingDetailItem ||
                                                                                                                                    loadingDetailField === "quantity"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingDetailItem && loadingDetailField === "quantity" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "13%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        {amountLabelBody(detailSubItem?.sub_item_detail_data?.material_cost || 0)}
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "13%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        <div className="table_input_group">
                                                                                                                            <InputNumber
                                                                                                                                value={values.labour ?? 0}
                                                                                                                                onValueChange={(e: InputNumberValueChangeEvent) =>
                                                                                                                                    handleDetailSubItemValueChange(detailSubItem.id, "labour", e.value ?? null)
                                                                                                                                }
                                                                                                                                onBlur={() => updateDetailSubItem(detailSubItem, 'labour')}
                                                                                                                                prefix={currencySymbol}
                                                                                                                                className="table_input"
                                                                                                                                minFractionDigits={2}
                                                                                                                                maxFractionDigits={2}
                                                                                                                                placeholder="0.00"
                                                                                                                                useGrouping
                                                                                                                                mode="decimal"
                                                                                                                                readOnly={
                                                                                                                                    !isEditingDetailItem ||
                                                                                                                                    isUpdatingDetailItem ||
                                                                                                                                    loadingDetailField === "labour"
                                                                                                                                }
                                                                                                                            />

                                                                                                                            {isUpdatingDetailItem && loadingDetailField === "labour" && (
                                                                                                                                <ProgressSpinner
                                                                                                                                    className="progresscircle custom input_loading"
                                                                                                                                    style={{ width: '18px', height: '18px', stroke: '#8953D7' }}
                                                                                                                                    strokeWidth="6"
                                                                                                                                    fill="none"
                                                                                                                                    animationDuration="1s"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "13%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        {amountLabelBody(detailSubItem?.final_total || 0)}
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{ width: "13%" }}
                                                                                                                        className="text_no_wrap item_value_column"
                                                                                                                    >
                                                                                                                        {detailSubItemActionBody(detailSubItem)}
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            )
                                                                                                        })}
                                                                                                    </table>
                                                                                                ) : (
                                                                                                    <table className="table pck_track_list_table">
                                                                                                        <tr className="part_item_sub_row">
                                                                                                            <td className="text-center empty_data_column" colSpan={9}>
                                                                                                                <i className="bi bi-exclamation-circle"></i>
                                                                                                                No items have been added, click the Add button to add an item.
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    </table>
                                                                                                )}
                                                                                            </motion.div>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </AnimatePresence>
                                                                        </React.Fragment>
                                                                    )
                                                                })}
                                                            </React.Fragment>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </article>
                            </React.Fragment>
                        ) : quotationDetailViewState === 'Terms' ? (
                            <React.Fragment>
                                <article className="main_section_area">
                                    <div className="input_label_flex_area">
                                        {/* Advance payment */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Advance payment:</h6>

                                                <div className="display_input">
                                                    {quotationDetail?.term_data?.advance_payment ? (
                                                        <span>
                                                            {currencySymbol}&nbsp;{(quotationDetail?.term_data?.advance_payment).toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            {currencySymbol}&nbsp;0.00
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {(canEdit) && (
                                                <React.Fragment>
                                                    {quotationDetail?.term_data?.advance_payment ? (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleEditTerm('Advance payment')}>
                                                            <i className="bi bi-pencil-square"></i>
                                                            Edit
                                                            <Ripple />
                                                        </button>
                                                    ) : (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleAddTerm('Advance payment')}>
                                                            <i className="bi bi-plus-circle"></i>
                                                            Add
                                                            <Ripple />
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )}

                                        </div>

                                        {/* Start date */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Start date:</h6>

                                                <div className="display_input">
                                                    {quotationDetail?.term_data?.start_date ? (
                                                        <span>
                                                            {quotationDetail?.term_data?.start_date}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            dd/mm/yyyy
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {(canEdit) && (
                                                <React.Fragment>
                                                    {quotationDetail?.term_data?.start_date ? (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleEditTerm('Start date')}>
                                                            <i className="bi bi-pencil-square"></i>
                                                            Edit
                                                            <Ripple />
                                                        </button>
                                                    ) : (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleAddTerm('Start date')}>
                                                            <i className="bi bi-plus-circle"></i>
                                                            Add
                                                            <Ripple />
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )}


                                        </div>

                                        {/* Duration */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Duration:</h6>

                                                <div className="display_input">
                                                    {(quotationDetail?.term_data?.duration_type && quotationDetail?.term_data?.duration) ? (
                                                        <span>
                                                            {quotationDetail?.term_data?.duration_type[0]}&nbsp;&nbsp;
                                                            {String(quotationDetail?.term_data?.duration).padStart(2, '0')}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            W&nbsp;&nbsp;00
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {(canEdit) && (
                                                <React.Fragment>
                                                    {(quotationDetail?.term_data?.duration_type && quotationDetail?.term_data?.duration) ? (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleEditTerm('Duration')}>
                                                            <i className="bi bi-pencil-square"></i>
                                                            Edit
                                                            <Ripple />
                                                        </button>
                                                    ) : (
                                                        <button className="add_data_btn p-ripple" onClick={() => handleAddTerm('Duration')}>
                                                            <i className="bi bi-plus-circle"></i>
                                                            Add
                                                            <Ripple />
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )}

                                        </div>
                                    </div>
                                </article>
                            </React.Fragment>
                        ) : null}
                    </div>
                </React.Fragment>
            )}


            {/* Cover page preview */}
            <Dialog
                visible={showCoverPagePreviewModal}
                header={coverPagePreviewModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseCoverPagePreviewModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    {coverPageData && coverPageData !== null && (
                        <div className="cover_page_area">
                            <img src={coverPageData?.comapany_logo || '/assets/images/icons/no_img.svg'} alt='' />

                            {coverPageData?.project_description && (
                                <p>{coverPageData?.project_description || '----'}</p>
                            )}

                            <h5><span>At</span></h5>
                            <h5>{coverPageData?.project_address || '----'}</h5>
                            <br />

                            <h5><span>For</span></h5>
                            <h5>
                                {coverPageData?.client_name || '---'}
                            </h5>
                        </div>
                    )}
                </div>
            </Dialog>
            {/*  */}


            {/* Note modal */}
            <Dialog
                visible={showNoteModal}
                header={noteModalHeader}
                footer={noteModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseNoteModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pt-2 pt-sm-2 pb-sm-0 w-100">
                        <div className="page_form_group mb-sm-3 mb-2">
                            <div className="form_label_grp mb-1">
                                <label htmlFor="note" className="custom_form_label is_required">Note:</label>

                                {dataState === 'Add' && quotationNotesData && quotationNotesData?.length > 0 && (
                                    <button className="add_data_btn p-ripple" onClick={handleViewNoteList}>
                                        <i className="bi bi-plus-circle"></i>
                                        Choose
                                        <Ripple />
                                    </button>
                                )}
                            </div>

                            <InputTextarea
                                id="note"
                                value={note}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                                className="custom_form_input is_text_area"
                                placeholder="Note"
                                autoComplete="off"
                            />

                            {noteError && <small className="form_error_msg">{noteError}</small>}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Note list modal */}
            <Dialog
                visible={showNoteListModal}
                header={noteListModalHeader}
                footer={referenceModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseNoteListModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body overflow-hidden">
                    <div className="row">
                        {errorQuotationNoteList ? (
                            <div className="col-12">
                                <div className="feature_not_available_area mb-0">
                                    <h6>An error occurred while retrieving notes data.</h6>
                                </div>
                            </div>
                        ) : (
                            <React.Fragment>
                                {quotationNotesData && quotationNotesData?.length > 0 ? (
                                    <div className="col-12">
                                        <ul className="reference_list_area">
                                            {quotationNotesData?.map((data, index) => {
                                                const isSelected = selectedNotes.some(
                                                    (item) => item.id === data.id
                                                );

                                                const isnoteAdded = quotationDetail?.note_list?.some(e => e?.note_default_id === data?.id);

                                                return (
                                                    <li
                                                        key={data?.id}
                                                        className="reference_list_item"
                                                    >
                                                        <div className="reference_item_grp">
                                                            <p>
                                                                <span className="text-dark">{index + 1}.&nbsp;</span>
                                                                {data?.note}
                                                            </p>
                                                            {isnoteAdded && (
                                                                <span className="reference_info info">
                                                                    <i className="bi bi-info-circle me-1"></i>
                                                                    Already added.
                                                                </span>
                                                            )}
                                                        </div>

                                                        <Checkbox
                                                            inputId={`reference_${data?.id}`}
                                                            checked={isSelected}
                                                            onChange={(e: CheckboxChangeEvent) => {
                                                                if (e.checked) {
                                                                    setSelectedNotes((prev) => [...prev, data]);
                                                                } else {
                                                                    setSelectedNotes((prev) =>
                                                                        prev.filter((item) => item.id !== data.id)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="col-12">
                                        <div className="feature_not_available_area mb-0">
                                            <h6>No notes available.</h6>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Simple item modal */}
            <Dialog
                visible={showSimpleItemModal}
                header={simpleItemModalHeader}
                footer={simpleItemModalFooter}
                headerClassName="custom_modal_header"
                className={`custom_modal_dialog modal_dialog_sm`}
                onHide={handleCloseSimpleItemModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 px-sm-2 py-sm-0">
                        <div className="row">
                            {/*Item name */}
                            <div className="col-12">
                                <TextInput
                                    id="itemName"
                                    label="Item name"
                                    labelHtmlFor="itemName"
                                    required={true}
                                    value={simpleItemName}
                                    placeholder="Enter item name"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSimpleItemName(e.target.value)}
                                    error={simpleItemNameError}
                                    formGroupClassName={dataState === 'Add' ? 'mb-2' : ''}
                                />
                            </div>

                            {dataState === 'Edit' && (
                                <React.Fragment>
                                    {/* Material */}
                                    <div className="col-12 col-sm-6">
                                        <NumberInput
                                            inputId="material"
                                            label="Material"
                                            labelHtmlFor="material"
                                            placeholder="0"
                                            onChange={handleMaterialChange}
                                            value={material}
                                            required={true}
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            prefix={currencySymbol + " "}
                                            min={0}
                                            error={materialError}
                                            inputDisabled={materialDisabled}
                                        />
                                    </div>

                                    {/* Labour */}
                                    <div className="col-12 col-sm-6">
                                        <NumberInput
                                            inputId="labour"
                                            label="Labour"
                                            labelHtmlFor="labour"
                                            placeholder="0"
                                            onChange={handleLabourChange}
                                            value={labour}
                                            required={true}
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            prefix={currencySymbol + " "}
                                            min={0}
                                            error={labourError}
                                            inputDisabled={labourDisabled}
                                        />
                                    </div>

                                    {/* Total */}
                                    <div className="col-12 col-sm-6">
                                        <NumberInput
                                            inputId="total"
                                            label="Total"
                                            labelHtmlFor="total"
                                            placeholder="0"
                                            onChange={handleTotalChange}
                                            value={total}
                                            required={true}
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            prefix={currencySymbol + " "}
                                            min={0}
                                            error={totalError}
                                            inputDisabled={totalDisabled}
                                        />
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Detail item modal */}
            <Dialog
                visible={showDetailItemModal}
                header={detailItemModalHeader}
                footer={detailItemModalFooter}
                headerClassName="custom_modal_header"
                className={`custom_modal_dialog modal_dialog_sm`}
                onHide={handleCloseDetailItemModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 px-sm-2 py-sm-0">
                        <div className="row">
                            {/* Sub item name */}
                            <div className="col-12">
                                <TextInput
                                    id="detailItemName"
                                    label="Sub item"
                                    labelHtmlFor="detailItemName"
                                    required={true}
                                    value={detailItemName}
                                    placeholder="Enter sub item name"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetailItemName(e.target.value)}
                                    error={detailItemNameError}
                                />
                            </div>

                            {/* Unit */}
                            <div className="col-12 col-sm-6">
                                <div className="page_form_group">
                                    <label htmlFor="unit" className="custom_form_label is_required">Unit</label>
                                    <Dropdown
                                        id="unit"
                                        value={unit}
                                        onChange={(e: DropdownChangeEvent) => setUnit(e.value)}
                                        options={materialDataUnits}
                                        optionLabel="unit_name"
                                        placeholder="Select unit"
                                        className="form_dropdown w-100"
                                        filter
                                        filterPlaceholder="Search unit"
                                        loading={loadingMaterialDataUnit}
                                        showClear
                                    />

                                    {unitError &&
                                        <small className="form_error_msg">{unitError}</small>
                                    }
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="col-12 col-sm-6">
                                <NumberInput
                                    inputId="quantity"
                                    label="Quantity"
                                    labelHtmlFor="quantity"
                                    placeholder="Quantity"
                                    onChange={handleQuantityChange}
                                    value={quantity}
                                    required={true}
                                    min={0}
                                    error={quantityError}
                                />
                            </div>

                            {/* Unit price */}
                            <div className="col-12 col-sm-6">
                                <NumberInput
                                    inputId="unitPrice"
                                    label="Unit price"
                                    labelHtmlFor="unitPrice"
                                    placeholder="Unit price"
                                    onChange={handleUnitPriceChange}
                                    value={unitPrice}
                                    required={true}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    prefix={currencySymbol + " "}
                                    min={0}
                                    error={unitPriceError}
                                />
                            </div>

                            {/* Material cost */}
                            <div className="col-12 col-sm-6">
                                <NumberInput
                                    inputId="materialCost"
                                    label="Material cost"
                                    labelHtmlFor="materialCost"
                                    placeholder="Material cost"
                                    onChange={handleMaterialCostChange}
                                    value={materialCost}
                                    required={false}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    prefix={currencySymbol + " "}
                                    min={0}
                                    inputDisabled
                                    error={materialCostError}
                                />
                            </div>

                            {/* Labour cost */}
                            <div className="col-12 col-sm-6">
                                <NumberInput
                                    inputId="labourCost"
                                    label="Labour cost"
                                    labelHtmlFor="labourCost"
                                    placeholder="Labour cost"
                                    onChange={handleLabourCostChange}
                                    value={labourCost}
                                    required={false}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    prefix={currencySymbol + " "}
                                    min={0}
                                    error={labourCostError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Terms modal */}
            <Dialog
                visible={showTermsModal}
                header={termModalHeader}
                footer={termModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_xsm"
                onHide={handleCloseTermsModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-2 pt-2 pb-sm-0 w-100">
                        {termsAddState === 'Advance payment' && (
                            <NumberInput
                                inputId="advancePayment"
                                label="Payment"
                                labelHtmlFor="advancePayment"
                                placeholder="Advance payment"
                                onChange={handleAdvancePaymentChange}
                                value={advancePayment}
                                required={true}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                prefix={currencySymbol + " "}
                                min={0}
                                error={advancePaymentError}
                                formGroupClassName="mb-2 mb-sm-3"
                            />
                        )}

                        {termsAddState === 'Start date' && (
                            <DatePicker
                                key={`startDate`}
                                id="startDate"
                                label="Start date"
                                labelHtmlFor="startDate"
                                required={true}
                                value={startDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setStartDate}
                                error={startDateError}
                                showClear
                                onClear={() => setStartDate(null)}
                                formGroupClassName="mb-2 mb-sm-3"
                            />
                        )}

                        {termsAddState === 'Duration' && (
                            <React.Fragment>
                                <div className="page_radio_option_group mb-4">
                                    <div className="page_radio_option_item">
                                        <RadioButton inputId="week" name="durationOption" value="Week"
                                            onChange={(e: RadioButtonChangeEvent) => setDurationOption(e.value)}
                                            checked={durationOption === 'Week'} />
                                        <label htmlFor="week">Week</label>
                                    </div>

                                    <div className="page_radio_option_item">
                                        <RadioButton inputId="month" name="durationOption" value="Month"
                                            onChange={(e: RadioButtonChangeEvent) => setDurationOption(e.value)}
                                            checked={durationOption === 'Month'} />
                                        <label htmlFor="month">Month</label>
                                    </div>
                                </div>

                                <NumberInput
                                    inputId="duration"
                                    label="Duration"
                                    labelHtmlFor="duration"
                                    placeholder="Duration"
                                    onChange={handleDurationChange}
                                    value={duration}
                                    required={true}
                                    min={0}
                                    error={durationError}
                                    formGroupClassName="mb-2 mb-sm-3"
                                />
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Quotation report config modal */}
            <QuotationReportConfigModal
                isOpen={showReportConfigModal}
                onClose={handleCloseReportConfigModal}
                toast={toast}
                reportTitle={sowViewOption + ` report`}
                quotationNo={quotationNo}
                quotationDetail={quotationDetail}
                view={sowViewOption}
                quotation={quotation}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default QuotationDetail;